import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { HttpService } from "../http/http.service";
import {
    EditIntegrationInputModel,
    IntegrationCreationResponseModel,
    IntegrationCredentials,
    IntegrationModel,
    IntegrationToAddModel,
    StatusCodeEnum,
    IntegrationCredentialUpdateModel,
    IntegrationAuthenticationUpdateModel,
    IntegrationsScansResponse,
} from "src/app/models/api/integration.model";
import { AuthenticationMethod } from "src/app/models/api";

@Injectable({
    providedIn: "root",
})
export class IntegrationsHttpService {
    private readonly _integrationsBaseUrl: string = "integrations";

    constructor(private _httpService: HttpService) {}

    public getIntegrationsDetails$(): Observable<IntegrationModel[]> {
        const url = `${this._integrationsBaseUrl}`;
        return this._httpService.get<IntegrationModel[]>(url);
    }

    public getIntegrationsScans$(): Observable<IntegrationsScansResponse> {
        const url = `${this._integrationsBaseUrl}/scans/last`;
        return this._httpService.get<IntegrationsScansResponse>(url);
    }

    public getIntegration(integrationId: string): Observable<IntegrationModel> {
        const url = `${this._integrationsBaseUrl}/${integrationId}`;
        return this._httpService.get<IntegrationModel>(url);
    }

    public getIntegrationCredentials(integrationId: string): Observable<IntegrationCredentials> {
        const url = `${this._integrationsBaseUrl}/${integrationId}/credentials`;
        return this._httpService.get<IntegrationCredentials>(url);
    }

    public editIntegration(integrationId: string, input: EditIntegrationInputModel): Observable<string> {
        const url = `${this._integrationsBaseUrl}/${integrationId}`;
        return this._httpService.put<string, EditIntegrationInputModel>(url, input);
    }

    public deleteIntegration(integrationId: string): Observable<string> {
        const url = `${this._integrationsBaseUrl}/${integrationId}`;
        return this._httpService.delete<string>(url);
    }

    public disableIntegration(integrationId: string): Observable<string> {
        return this._setIntegrationActivation(integrationId, StatusCodeEnum.off);
    }

    public enableIntegration(integrationId: string): Observable<string> {
        return this._setIntegrationActivation(integrationId, StatusCodeEnum.on);
    }

    public addIntegration(input: IntegrationToAddModel): Observable<IntegrationCreationResponseModel> {
        return this._httpService.post<IntegrationToAddModel, IntegrationCreationResponseModel>(this._integrationsBaseUrl, input);
    }

    public retryIntegration(integrationId: string): Observable<undefined> {
        const url = `${this._integrationsBaseUrl}/${integrationId}/retry`;
        return this._httpService.post(url, {});
    }

    public editIntegrationAuthentication(
        integrationId: string,
        authenticationMethod: AuthenticationMethod,
        credentials: IntegrationCredentials,
    ): Observable<string> {
        const url = `${this._integrationsBaseUrl}/${integrationId}/authentication`;
        return this._httpService.put<string, IntegrationAuthenticationUpdateModel>(url, { authenticationMethod, credentials });
    }

    public editIntegrationCredentials(
        integrationId: string,
        credentials_without_secrets: IntegrationCredentials,
        secret_credentials: IntegrationCredentials,
    ): Observable<string> {
        const url = `${this._integrationsBaseUrl}/${integrationId}/credentials`;
        return this._httpService.put<string, IntegrationCredentialUpdateModel>(url, {
            credentials_without_secrets: credentials_without_secrets,
            secret_credentials: secret_credentials,
        });
    }

    private _setIntegrationActivation(integrationId: string, statusCode: StatusCodeEnum): Observable<string> {
        const url = `${this._integrationsBaseUrl}/${integrationId}/activation?status=${statusCode}`;
        return this._httpService.put<string>(url);
    }
}
